import { useCallback, useEffect, useState } from 'react';

/**
 * Detects overflow on the observed element and re-checks on size changes.
 *
 * Both axes are tested: `truncate` (`white-space: nowrap`) overflows
 * horizontally, while `line-clamp-*` wraps and overflows vertically, so a
 * width-only check reports clamped text as untruncated.
 */
export function useIsTruncated() {
  const [node, setObservedNode] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // React Router's `<Link>` merges this into a new ref on every render, so React detaches it
  // (`null`) and reattaches the same element each time. Storing that `null` would cost a render
  // per render, and a burst of header renders then exceeds React's nested-update limit. Keeping
  // the last element instead lets the reattach bail out. Callers pass this straight to `ref`, so
  // it must keep its identity.
  const setNode = useCallback((element: HTMLElement | null) => {
    if (element) setObservedNode(element);
  }, []);

  useEffect(() => {
    if (!node) return;

    const check = () =>
      setIsTruncated(node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { setNode, isTruncated };
}
